import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import { API } from '../api';

export default function AdminErrorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/errors?limit=200${filter ? `&context=${encodeURIComponent(filter)}` : ''}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setItems(d.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const contexts = Array.from(new Set(items.map((x) => x.context))).sort();

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Admin · Client errors' }]} />
        <div className="mt-3 mb-5"><DataFreshnessStrip compact={false} /></div>

        <header className="mt-6 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#C75D5D]/30 bg-white">
            <AlertTriangle size={12} className="text-[#C75D5D]" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#C75D5D]">Internal · Client error feed</span>
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
            What is breaking in the browser?
          </h1>
          <p className="mt-3 text-[14px] text-[#3A4A44] leading-relaxed">
            Live tail of <code>console.warn</code>/<code>reportError</code> events flushed from every visitor.
            Capped at 2,000 entries (oldest auto-evicted).
          </p>
        </header>

        <div className="mt-5 flex items-center flex-wrap gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="errors-refresh">
            <RefreshCw size={13}/> Refresh
          </button>
          <button onClick={() => setFilter('')} className={`h-9 px-3 rounded-md text-[12.5px] ${filter === '' ? 'bg-[#4A7C5E] text-white' : 'bg-white border border-[#E5DCC4] text-[#3A4A44]'}`}>All</button>
          {contexts.slice(0, 12).map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`h-9 px-3 rounded-md text-[12.5px] ${filter === c ? 'bg-[#4A7C5E] text-white' : 'bg-white border border-[#E5DCC4] text-[#3A4A44]'}`}>
              {c}
            </button>
          ))}
          <span className="ml-auto text-[11.5px] text-[#7A857F]">{items.length} events shown</span>
        </div>

        {loading && <div className="mt-6 text-sm text-[#7A857F]">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="mt-6 rounded-xl border border-dashed border-[#E5DCC4] p-8 text-center bg-white">
            <div className="text-[13px] text-[#7A857F]">No client errors recorded. (Either you're winning, or no users have hit this build yet.)</div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5 w-44">Time</th>
                  <th className="text-left px-4 py-2.5 w-44">Context</th>
                  <th className="text-left px-4 py-2.5">Message</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">URL</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.ts}-${i}`} className="border-t border-[#F0E7D2]">
                    <td className="px-4 py-2.5 text-[#5A6660] tabular-nums whitespace-nowrap">{it.ts ? new Date(it.ts).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-block px-2 py-0.5 rounded-md bg-[#FBE6E0] text-[#C75D5D] text-[11px] font-medium">{it.context}</span>
                    </td>
                    <td className="px-4 py-2.5 text-[#1A2622]">{it.message}</td>
                    <td className="px-4 py-2.5 text-[#7A857F] hidden md:table-cell truncate max-w-[300px]" title={it.url}>{it.url?.replace(/^https?:\/\/[^/]+/, '') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <p className="mt-4 text-[11.5px] text-[#A8B0AA]">
          Source: <code>POST /api/log/error</code> → MongoDB capped collection <code>client_errors</code> (max 2,000 entries, ~1 MB).
        </p>
      </main>
      <Footer />
    </div>
  );
}
